import React, { useState, useEffect } from 'react';
import { Wifi, TreePine, Thermometer, Star, Car, Plane, Maximize2, MapPin } from 'lucide-react';
import { heroImages, apartmentInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const scrollToReservation = (e) => {
    e.preventDefault();
    const element = document.querySelector('#rezervacija');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Images with Smooth Crossfade */}
      {heroImages.map((img, index) => (
        <div
          key={index}
          className="absolute inset-0"
          style={{
            opacity: index === currentImageIndex ? 1 : 0,
            transition: 'opacity 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: index === currentImageIndex ? 1 : 0,
          }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${img})`,
              transform: index === currentImageIndex ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 8s ease-out',
            }}
          />
        </div>
      ))}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 z-10" />

      {/* Content */}
      <div className="relative z-20 min-h-screen flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        {/* Welcome Text */}
        <p className="text-amber-400 text-sm md:text-base font-medium tracking-wider mb-3 animate-fade-in">
          {t.hero.welcome}
        </p>
        
        {/* Main Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-2 leading-tight">
          <span className="block">{apartmentInfo.name}</span>
          <span className="block text-xl sm:text-2xl md:text-3xl font-light text-amber-400 mt-1">
            {apartmentInfo.subtitle}
          </span>
        </h1>

        {/* Booking.com Rating Badge - PROMINENT with link to reviews */}
        <a 
          href={apartmentInfo.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mt-4 mb-4 group cursor-pointer"
          data-testid="booking-reviews-link"
        >
          <div className="flex items-center gap-2 bg-amber-500 group-hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-bold shadow-lg transition-all duration-300 group-hover:scale-105">
            <Star size={20} fill="white" />
            <span className="text-lg">{t.hero.rating}</span>
          </div>
          <span className="text-white/90 text-sm sm:text-base group-hover:text-white underline-offset-2 group-hover:underline transition-all duration-300">
            {t.hero.reviews} →
          </span>
        </a>

        {/* Key Benefits - Visible immediately */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
          <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs sm:text-sm">
            <Car size={14} className="text-amber-400" />
            <span>{t.hero.parking}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs sm:text-sm">
            <Plane size={14} className="text-amber-400" />
            <span>{t.hero.airportTransfer}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs sm:text-sm">
            <Maximize2 size={14} className="text-amber-400" />
            <span>{t.hero.size}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs sm:text-sm">
            <MapPin size={14} className="text-amber-400" />
            <span>{t.hero.centralLocation}</span>
          </div>
        </div>

        {/* Tagline/Description */}
        <p className="text-white/90 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-6 px-2">
          {t.hero.tagline}
        </p>

        {/* CTA Button */}
        <button
          onClick={scrollToReservation}
          data-testid="hero-cta-button"
          className="bg-amber-700 hover:bg-amber-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-medium transition-all duration-300 shadow-2xl hover:shadow-amber-700/30 hover:scale-105 mb-8"
        >
          {t.hero.cta}
        </button>

        {/* Additional Features */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
          {[
            { icon: TreePine, label: t.hero.parkView },
            { icon: Wifi, label: t.hero.freeWifi },
            { icon: Thermometer, label: t.hero.climate },
          ].map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full text-white text-xs sm:text-sm"
            >
              <feature.icon size={16} className="text-amber-400" />
              <span>{feature.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Image Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
        {heroImages.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-500 ${
              index === currentImageIndex 
                ? 'bg-amber-400 w-8' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
